/**
 * Keeping an unsaved view alive while somebody is editing it.
 *
 * An unsaved view lives in server memory and expires a fixed grace after its last
 * touch (TEMP_VIEW_TTL_MS, five minutes). The editor used to touch it only by editing
 * it, so a user who opened Build View and then stopped to think lost the entry: the
 * next autosave 404'd under a red "Save failed", and a reload said the view could not
 * be opened. The work was gone and nothing had gone wrong except the passage of time.
 *
 * This is the missing signal, deliberately kept dumb: a bare "still open" on a slow
 * timer that carries no content, so it cannot overwrite an edit, cannot race the
 * autosave, and costs one tiny request a minute against a five-minute grace — four
 * beats may be lost in a row before anything expires.
 *
 * Nothing is imported here on purpose. The scheduler and the beat are both injected,
 * which is what lets the check drive years of simulated time in a millisecond instead
 * of sleeping past a five-minute TTL.
 */

/** Ids minted by the temp registry carry this; a saved view has a row instead. */
const TEMP_PREFIX = "t_";

/**
 * How often a mounted editor says "still open".
 *
 * A fifth of the TTL. The margin is the point — a beat may be missed four times over
 * (a sleeping laptop, a throttled background tab, a flaky link) before the view is
 * actually at risk, and at one request a minute this is two orders of magnitude
 * quieter than the 1.2s autosave it must not turn into.
 */
export const VIEW_KEEPALIVE_MS = 60_000;

/** Start a repeating beat and hand back the way to stop it. */
type Schedule = (beat: () => void, intervalMs: number) => () => void;

const byTimer: Schedule = (beat, intervalMs) => {
  const handle = setInterval(beat, intervalMs);
  return () => clearInterval(handle);
};

/**
 * Beat "still open" at the server for as long as the caller keeps the returned stop
 * function unused — i.e. for as long as the editor is mounted.
 *
 * A saved view has a row of its own and nothing to keep alive, so it is not beaten
 * for at all: no timer is even created, which is why the stop function is safe to
 * call unconditionally.
 */
export function startViewKeepAlive(
  viewId: string,
  touch: (id: string) => void,
  {
    intervalMs = VIEW_KEEPALIVE_MS,
    schedule = byTimer,
  }: { intervalMs?: number; schedule?: Schedule } = {},
): () => void {
  if (!viewId.startsWith(TEMP_PREFIX)) return () => {};
  return schedule(() => touch(viewId), intervalMs);
}
